/**
 * Formata origem/destino de transações para exibição (sem UUID de tenant).
 * Tipos: I Entrada, O Saída, L Posição, R Reserva, B Bloqueio.
 */
import db from '../../versa/db.js';

export function parseTransactionEndpoint(raw)
{
	if (raw == null || raw === '')
		return null;
	if (typeof raw === 'object')
		return { ...raw };
	try
	{
		return JSON.parse(String(raw));
	}
	catch
	{
		return { _unparsed: String(raw) };
	}
}

/** Remove campos de tenant / redundantes na UI. */
export function scrubEndpointForDisplay(parsed)
{
	if (!parsed || typeof parsed !== 'object')
		return {};
	const o = { ...parsed };
	delete o.company_id;
	delete o.input_company_id;
	delete o.lot_company_id;
	return o;
}

function extrasSuffix(o)
{
	const bits = [];
	if (o.replenishment_id != null)
		bits.push(`Ressup.#${o.replenishment_id}`);
	if (o.inventory_id != null)
		bits.push(`Inv.#${o.inventory_id}`);
	return bits.length ? ` · ${bits.join(' · ')}` : '';
}

async function inputLinkedLabel(rowCompanyId, inputId)
{
	if (inputId == null)
		return '';
	try
	{
		const e = await db.get('inputs', [rowCompanyId, inputId]);
		if (!e)
			return '';
		if (e.invoice_number)
			return ` · NF ${e.invoice_number}`;
		if (e.order_id)
			return ` · Pedido ${e.order_id}`;
		return '';
	}
	catch
	{
		return '';
	}
}

async function outputLinkedLabel(rowCompanyId, outputId)
{
	if (outputId == null)
		return '';
	try
	{
		const e = await db.get('outputs', [rowCompanyId, outputId]);
		if (!e)
			return '';
		if (e.reference)
			return String(e.reference).trim();
		if (e.order_id)
			return `Pedido ${e.order_id}`;
		return '';
	}
	catch
	{
		return '';
	}
}

/**
 * @param {function} keyFn async (store, keys, label) => string — ex.: this.key.bind(this)
 * @param {string} rowCompanyId company_id da linha (transação)
 */
export async function formatTransactionEndpointDisplay(keyFn, rowCompanyId, typeChar, raw)
{
	const parsed = parseTransactionEndpoint(raw);
	if (!parsed)
		return '';
	if (parsed._unparsed)
		return parsed._unparsed;

	const scopeCompany = parsed.company_id || rowCompanyId;
	const o = scrubEndpointForDisplay(parsed);

	switch (typeChar)
	{
		case 'I':
		{
			if (o.input_id == null)
				return fallbackObj(o);
			const tail = await inputLinkedLabel(rowCompanyId, o.input_id);
			return tail ? `Entrada${tail}` : 'Entrada';
		}
		case 'O':
		{
			if (o.output_id == null)
				return fallbackObj(o);
			const ref = await outputLinkedLabel(rowCompanyId, o.output_id);
			return ref ? `Saída · ${ref}` : 'Saída';
		}
		case 'L':
		case 'B':
		{
			const wh = o.warehouse_id;
			const loc = o.location_id;
			if (wh == null || loc == null)
				return fallbackObj(o);
			const whName = keyFn
				? ((await keyFn('warehouses', [scopeCompany, wh], 'name')) || `Arm.${wh}`)
				: `Arm.${wh}`;
			const addr = keyFn
				? ((await keyFn('locations', [scopeCompany, wh, loc], 'address')) || '')
				: '';
			const locPart = addr || '(endereço não cadastrado)';
			const inExtra = o.input_id != null ? await inputLinkedLabel(rowCompanyId, o.input_id) : '';
			return `${whName} · ${locPart}${extrasSuffix(o)}${inExtra}`;
		}
		case 'R':
		{
			const parts = [];
			const wh = o.warehouse_id;
			const loc = o.location_id;
			if (wh != null && loc != null)
			{
				const whName = keyFn
					? ((await keyFn('warehouses', [scopeCompany, wh], 'name')) || `Arm.${wh}`)
					: `Arm.${wh}`;
				const addr = keyFn
					? ((await keyFn('locations', [scopeCompany, wh, loc], 'address')) || '')
					: '';
				parts.push(`${whName} · ${addr || '(endereço não cadastrado)'}`);
			}
			if (o.output_id != null)
			{
				const ref = await outputLinkedLabel(rowCompanyId, o.output_id);
				parts.push(ref ? `Saída · ${ref}` : 'Saída');
			}
			let s = parts.join(' · ') + extrasSuffix(o);
			if (o.input_id != null)
				s += await inputLinkedLabel(rowCompanyId, o.input_id);
			return s || fallbackObj(o);
		}
		default:
			return fallbackObj(o);
	}
}

function fallbackObj(o)
{
	const keys = Object.keys(o);
	if (!keys.length)
		return '';
	return keys.map(k => `${k}: ${o[k]}`).join(', ');
}

/** Exportação / tooltip sem consulta ao db local. */
export function formatTransactionEndpointSync(rowCompanyId, typeChar, raw)
{
	const parsed = parseTransactionEndpoint(raw);
	if (!parsed)
		return '';
	if (parsed._unparsed)
		return parsed._unparsed;
	const o = scrubEndpointForDisplay(parsed);
	switch (typeChar)
	{
		case 'I':
			return o.input_id != null ? 'Entrada' : fallbackObj(o);
		case 'O':
			return o.output_id != null ? 'Saída' : fallbackObj(o);
		case 'L':
		case 'B':
		{
			const wh = o.warehouse_id;
			const loc = o.location_id;
			if (wh == null || loc == null)
				return fallbackObj(o);
			return `Arm.${wh} · (endereço não disponível offline)${extrasSuffix(o)}`;
		}
		case 'R':
		{
			const parts = [];
			if (o.warehouse_id != null && o.location_id != null)
				parts.push(`Arm.${o.warehouse_id} · (endereço não disponível offline)`);
			if (o.output_id != null)
				parts.push('Saída');
			let s = parts.join(' · ');
			if (o.replenishment_id != null)
				s += `${s ? ' · ' : ''}Ressup.#${o.replenishment_id}`;
			if (o.inventory_id != null)
				s += `${s ? ' · ' : ''}Inv.#${o.inventory_id}`;
			return s || fallbackObj(o);
		}
		default:
			return fallbackObj(o);
	}
}
